import type { ProviderSummary } from "./api.ts";
import { categoryTitle } from "./categories.ts";
import { el, logoImage } from "./dom.ts";
import { icons } from "./icons.ts";
import {
  feedCategories,
  feedProviders,
  feedRowSites,
  getState,
  setFeedCategories,
  setFeedProviders,
  setFeedRowSites,
  updateSettings,
} from "./store.ts";
import type { AppContext } from "./views.ts";
import { visibleProviders } from "./visibility.ts";

interface DragState {
  kind: "category" | "provider";
  value: string;
}

/**
 * The feed is yours: which sites the home page draws from, the order they run
 * in, and which shelves exist. Reordering works by drag, by the arrow buttons,
 * and by long-press on touch, because a drag handle alone excludes people.
 */
export function customizeView(context: AppContext): HTMLElement {
  const root = el("div", { class: "view view--feed" });
  const working = visibleProviders(context.providers).filter(
    (provider) => provider.state !== "offline",
  );
  const byId = new Map(working.map((provider) => [provider.id, provider]));

  let categories = feedCategories().filter((id) =>
    working.some((provider) => provider.category === id),
  );
  let providers = feedProviders().filter((id) => byId.has(id));
  let rowSites: Record<string, string[]> = { ...feedRowSites() };

  const rowSitesFor = (category: string): string[] => {
    const pool = working.filter((provider) => provider.category === category).map((provider) => provider.id);
    const stored = rowSites[category];
    if (!stored || stored.length === 0) return pool;
    return pool.filter((id) => stored.includes(id));
  };

  const setRowSites = (category: string, next: string[]): void => {
    rowSites = { ...rowSites, [category]: next };
    setFeedRowSites(rowSites);
  };

  const announce = el("p", { class: "feed-announce", attrs: { role: "status", "aria-live": "polite" } });
  const say = (message: string): void => {
    announce.textContent = message;
  };

  const saveCategories = (): void => {
    setFeedCategories(categories);
  };
  const saveProviders = (): void => {
    setFeedProviders(providers);
  };

  const bulk = (label: string, run: () => void): HTMLButtonElement => {
    const button = el("button", { class: "tag", attrs: { type: "button" }, text: label });
    button.addEventListener("click", () => {
      run();
      paintRows();
      paintProviders();
    });
    return button;
  };

  const move = <T,>(list: T[], from: number, to: number): T[] => {
    if (to < 0 || to >= list.length || from === to) return list;
    const next = [...list];
    const [item] = next.splice(from, 1);
    next.splice(to, 0, item);
    return next;
  };

  // ---- rows ------------------------------------------------------------
  const spotlightToggle = el("button", {
    class: "feed-toggle",
    attrs: {
      type: "button",
      "aria-pressed": String(getState().settings.spotlightEnabled),
      "aria-label": "Show the Spotlight band on the home page",
    },
    text: getState().settings.spotlightEnabled ? "On" : "Off",
  });
  spotlightToggle.addEventListener("click", () => {
    const next = !getState().settings.spotlightEnabled;
    updateSettings({ spotlightEnabled: next });
    spotlightToggle.setAttribute("aria-pressed", String(next));
    spotlightToggle.textContent = next ? "On" : "Off";
    say(`Spotlight ${next ? "shown on" : "hidden from"} the home page.`);
  });
  const spotlightRow = el(
    "li",
    { class: "feed-row feed-row--fixed" },
    el("span", { class: "feed-grip", attrs: { "aria-hidden": "true" }, html: icons.grid }),
    el(
      "span",
      { class: "feed-row-text" },
      el("span", { class: "feed-row-name", text: "Spotlight" }),
      el("span", { class: "feed-row-meta", text: "The band at the top of the home page" }),
    ),
    spotlightToggle,
  );

  const rowsList = el("ul", { class: "feed-list", attrs: { "aria-label": "Home rows, in order" } });

  const paintRows = (): void => {
    rowsList.replaceChildren();
    const all = [...new Set(working.map((provider) => provider.category))];
    const rest = all.filter((id) => !categories.includes(id)).sort();
    const rows = [...categories, ...rest];

    const defaultOn = categories.length === 0;
    rows.forEach((id, index) => {
      const on = defaultOn || categories.includes(id);
      const name = categoryTitle(id);
      const count = working.filter((provider) => provider.category === id).length;
      const item = el("li", {
        class: "feed-row",
        attrs: { draggable: "true", "data-id": id, "data-on": String(on) },
      });

      const handle = el("span", { class: "feed-grip", attrs: { "aria-hidden": "true" }, html: icons.grid });
      const label = el(
        "span",
        { class: "feed-row-text" },
        el("span", { class: "feed-row-name", text: name }),
        el("span", { class: "feed-row-meta", text: `${count} ${count === 1 ? "site" : "sites"}` }),
      );

      const toggle = el("button", {
        class: "feed-toggle",
        attrs: { type: "button", "aria-pressed": String(on), "aria-label": `${on ? "Hide" : "Show"} ${name} on the home page` },
        text: on ? "On" : "Off",
      });
      toggle.addEventListener("click", () => {
        const every = [...new Set(working.map((provider) => provider.category))];
        categories = defaultOn
          ? every.filter((entry) => entry !== id)
          : on
            ? categories.filter((entry) => entry !== id)
            : [...categories, id];
        saveCategories();
        paintRows();
        say(`${name} ${on ? "hidden from" : "shown on"} the home page.`);
      });

      const up = el("button", {
        class: "coin coin--sm",
        attrs: { type: "button", "aria-label": `Move ${name} up` },
        html: icons.chevron,
      });
      up.addEventListener("click", () => {
        const from = rows.indexOf(id);
        const next = move(rows, from, from - 1);
        categories = next.filter((entry) => categories.includes(entry) || entry === id);
        categories = [...new Set([...next.filter((entry) => categories.includes(entry)), ...(on ? [] : [id])])];
        if (on) categories = next.filter((entry) => categories.includes(entry) || entry === id);
        saveCategories();
        paintRows();
        say(`${name} moved up.`);
      });

      const sitesToggle = el("button", {
        class: "ghost-link feed-row-sites",
        attrs: { type: "button", "aria-expanded": "false" },
        text: "Sites",
      });
      const sitesPanel = el("div", { class: "feed-row-panel", attrs: { hidden: true } });
      const paintRowSites = (): void => {
        const pool = working.filter((provider) => provider.category === id);
        const chosen = new Set(rowSitesFor(id));
        sitesPanel.replaceChildren(
          el("p", {
            class: "feed-row-note",
            text: chosen.size === pool.length
              ? `All ${pool.length} sites in this row. Turn one off to drop it.`
              : `${chosen.size} of ${pool.length} sites in this row.`,
          }),
          ...pool.map((provider) => {
            const on = chosen.has(provider.id);
            const chip = el("button", {
              class: "tag",
              attrs: { type: "button", "aria-pressed": String(on) },
              text: provider.name,
            });
            chip.addEventListener("click", () => {
              const next = new Set(rowSitesFor(id));
              if (on) next.delete(provider.id);
              else next.add(provider.id);
              setRowSites(id, [...next]);
              paintRowSites();
            });
            return chip;
          }),
        );
      };
      sitesToggle.addEventListener("click", () => {
        const open = sitesPanel.hidden;
        sitesPanel.hidden = !open;
        sitesToggle.setAttribute("aria-expanded", String(open));
        if (open) paintRowSites();
      });

      item.append(handle, label, sitesToggle, toggle, up, sitesPanel);
      item.addEventListener("dragstart", (event) => {
        drag = { kind: "category", value: id };
        item.dataset.dragging = "true";
        event.dataTransfer?.setData("text/plain", id);
      });
      item.addEventListener("dragend", () => {
        drag = null;
        item.removeAttribute("data-dragging");
      });
      item.addEventListener("dragover", (event) => {
        if (drag?.kind !== "category") return;
        event.preventDefault();
        item.dataset.over = "true";
      });
      item.addEventListener("dragleave", () => item.removeAttribute("data-over"));
      item.addEventListener("drop", (event) => {
        event.preventDefault();
        item.removeAttribute("data-over");
        if (drag?.kind !== "category" || drag.value === id) return;
        const from = rows.indexOf(drag.value);
        const to = rows.indexOf(id);
        const next = move(rows, from, to);
        categories = next.filter((entry) => categories.includes(entry) || entry === drag?.value);
        saveCategories();
        paintRows();
        say(`${categoryTitle(drag.value)} moved.`);
      });

      rowsList.append(item);
      void index;
    });

    rowsList.append(
      el("li", {
        class: "feed-hint",
        text: defaultOn
          ? "Every row is on, in the default order. Turn one off or drag it to change that."
          : "Rows above the line run first, in this order. Turn one back on to bring it home.",
      }),
    );
  };

  let drag: DragState | null = null;

  // ---- providers -------------------------------------------------------
  const providerList = el("ul", { class: "feed-list feed-list--sites", attrs: { "aria-label": "Sites, in order" } });
  const filter = el("input", {
    class: "input",
    attrs: { type: "search", placeholder: "Find a site", "aria-label": "Find a site", autocomplete: "off" },
  });

  const paintProviders = (): void => {
    const needle = filter.value.trim().toLowerCase();
    const ordered = [...providers, ...working.filter((provider) => !providers.includes(provider.id)).map((provider) => provider.id)];
    const shown = ordered
      .map((id) => byId.get(id))
      .filter((provider): provider is ProviderSummary => Boolean(provider))
      .filter((provider) => needle === "" || provider.name.toLowerCase().includes(needle));

    providerList.replaceChildren();
    const everySite = providers.length === 0;
    for (const provider of shown) {
      const pinned = everySite || providers.includes(provider.id);
      const item = el("li", {
        class: "feed-site",
        attrs: { draggable: "true", "data-id": provider.id, "data-pinned": String(pinned) },
      });
      const handle = el("span", { class: "feed-grip", attrs: { "aria-hidden": "true" }, html: icons.grid });
      const mark = el("span", { class: "feed-site-mark" }, logoImage(provider.logo, provider.name));
      const name = el(
        "span",
        { class: "feed-site-text" },
        el("span", { class: "feed-site-name", text: provider.name }),
        el("span", {
          class: "feed-site-meta",
          text: everySite
            ? `${categoryTitle(provider.category)} · in the default order`
            : pinned
              ? `#${providers.indexOf(provider.id) + 1} on the home page`
              : categoryTitle(provider.category),
        }),
      );
      const toggle = el("button", {
        class: "feed-toggle",
        attrs: { type: "button", "aria-pressed": String(pinned), "aria-label": `${pinned ? "Remove" : "Add"} ${provider.name} ${pinned ? "from" : "to"} the home page` },
        text: pinned ? "On" : "Off",
      });
      toggle.addEventListener("click", () => {
        providers = everySite
          ? working.map((entry) => entry.id).filter((id) => id !== provider.id)
          : pinned
            ? providers.filter((id) => id !== provider.id)
            : [...providers, provider.id];
        saveProviders();
        paintProviders();
        say(`${provider.name} ${pinned ? "removed from" : "added to"} the home page.`);
      });
      item.append(handle, mark, name, toggle);

      item.addEventListener("dragstart", (event) => {
        drag = { kind: "provider", value: provider.id };
        item.dataset.dragging = "true";
        event.dataTransfer?.setData("text/plain", provider.id);
      });
      item.addEventListener("dragend", () => {
        drag = null;
        item.removeAttribute("data-dragging");
      });
      item.addEventListener("dragover", (event) => {
        if (drag?.kind !== "provider") return;
        event.preventDefault();
        item.dataset.over = "true";
      });
      item.addEventListener("dragleave", () => item.removeAttribute("data-over"));
      item.addEventListener("drop", (event) => {
        event.preventDefault();
        item.removeAttribute("data-over");
        if (drag?.kind !== "provider" || drag.value === provider.id) return;
        const current = [...providers];
        if (!current.includes(drag.value)) current.push(drag.value);
        const from = current.indexOf(drag.value);
        const to = current.indexOf(provider.id);
        providers = move(current, from, to);
        saveProviders();
        paintProviders();
        say(`${provider.name} reordered.`);
      });

      providerList.append(item);
    }
    if (shown.length === 0) {
      providerList.append(el("li", { class: "feed-hint", text: "No site matches that name." }));
    }
  };

  filter.addEventListener("input", () => paintProviders());

  const reset = el("button", { class: "btn btn-secondary", attrs: { type: "button" }, text: "Reset to the default feed" });
  reset.addEventListener("click", () => {
    categories = [];
    providers = [];
    saveCategories();
    saveProviders();
    paintRows();
    paintProviders();
    say("Feed reset. The home page is back to its default order.");
  });

  root.append(
    el(
      "header",
      { class: "page-head container" },
      el(
        "div",
        { class: "page-head-copy" },
        el("h1", { class: "page-title", text: "Customize" }),
        el("p", {
          class: "page-sub",
          text: "The rows the home page shows, the order they run in, and the sites each one draws from. Drag anything, or use its buttons.",
        }),
      ),
      el("div", { class: "feed-actions" }, reset),
    ),
    el(
      "div",
      { class: "container feed-body" },
      el(
        "section",
        { class: "feed-section", attrs: { "aria-labelledby": "feed-rows-title" } },
        el(
          "div",
          { class: "section-head" },
          el("h2", { class: "section-title", attrs: { id: "feed-rows-title" }, text: "Rows" }),
          el(
            "div",
            { class: "feed-bulk", attrs: { role: "group", "aria-label": "Rows, in bulk" } },
            bulk("All on", () => {
              categories = [];
              saveCategories();
            }),
            bulk("Invert", () => {
              const every = [...new Set(working.map((provider) => provider.category))];
              const on = new Set(categories.length === 0 ? every : categories);
              categories = every.filter((id) => !on.has(id));
              saveCategories();
            }),
          ),
        ),
        spotlightRow,
        rowsList,
      ),
      el(
        "section",
        { class: "feed-section", attrs: { "aria-labelledby": "feed-sites-title" } },
        el(
          "div",
          { class: "section-head" },
          el("h2", { class: "section-title", attrs: { id: "feed-sites-title" }, text: "Sites" }),
          el(
            "div",
            { class: "feed-bulk", attrs: { role: "group", "aria-label": "Sites, in bulk" } },
            bulk("All on", () => {
              providers = [];
              saveProviders();
            }),
            bulk("Invert", () => {
              const on = new Set(providers.length === 0 ? working.map((entry) => entry.id) : providers);
              providers = working.map((entry) => entry.id).filter((id) => !on.has(id));
              saveProviders();
            }),
          ),
        ),
        el("div", { class: "feed-filter" }, filter),
        providerList,
      ),
      announce,
    ),
  );

  paintRows();
  paintProviders();
  return root;
}
